<template>
  <FlotoCrudContainer
    ref="crudContainerRef"
    :fetch-fn="getWorkLogs"
    :update-fn="updateWorkLog"
    :scrollable="false"
    :create-fn="addWorkLog"
    :delete-fn="removeWorkLog"
    :default-item="defaultWorklog"
    :columns="tableColumns"
    :show-no-data="false"
    as-table
  >
    <template v-slot:add-controls="{ create, refreshList }">
      <MAffix v-if="disabled || (!disabled && !isAllowedWorklogAddAccess)">
        <div class="pb-1">
          <MCol class="flex items-center">
            <h6 class="px-1 font-semibold text-primary mb-0">
              {{ $t('work_log') }}
            </h6>
          </MCol>
        </div>
      </MAffix>
      <Component
        :is="stickySearch ? 'MAffix' : 'div'"
        v-if="!disabled"
        :offset-top="offsetTop"
        @change="searchAffixed = $event"
      >
        <div
          v-if="isAllowedWorklogAddAccess"
          :class="{ 'pt-2': searchAffixed }"
          class="sticky-tab-header"
        >
          <MRow class="flex justify-between items-center">
            <MCol class="flex items-center">
              <h6 class="px-1 font-semibold text-primary mb-0">
                {{ $t('work_log') }}
              </h6>
            </MCol>
            <MCol class="flex items-center justify-end">
              <slot name="before-add-controls"></slot>
              <MTooltip>
                <template v-slot:trigger>
                  <MButton
                    shape="circle"
                    variant="neutral-lighter"
                    :shadow="false"
                    class="mr-2"
                    :aria-label="$t('refresh')"
                    @click="refreshList"
                  >
                    <MIcon name="sync" size="md" />
                  </MButton>
                </template>
                {{ $t('refresh') }}
              </MTooltip>
              <MButton
                class="flex justify-center button-primary rounded items-center p-2"
                variant="neutral"
                :aria-label="$t('add')"
                @click="create"
              >
                <MIcon name="plus" size="lg" />
              </MButton>
            </MCol>
          </MRow>
          <MDivider class="mb-0" />
        </div>
      </Component>
    </template>
    <template v-slot:form-header="{ item }">
      {{ $t(item.id ? 'edit' : 'add') }} {{ $tc('work_log') }}
    </template>
    <template v-slot:form-items="{ item: worklog, resetForm }">
      <WorkLogForm
        :worklog="worklog"
        :group-id="groupId"
        :resource="resource"
        :technician="user"
        :custom-rules="customRules"
        :reset-form="resetForm"
      />
    </template>
    <template v-slot:form-actions="{ submit, cancel, item, processing }">
      <MButton
        id="add-update-btn"
        class="mx-1"
        :loading="processing"
        @click="submit"
      >
        {{
          $t(item.id ? 'update' : item.timeBasedWorkLog ? 'start_timer' : 'add')
        }}
      </MButton>
      <MButton id="cancel-btn" variant="default" @click="cancel">
        {{ $t('cancel') }}
      </MButton>
    </template>
    <template v-slot:technicianId="{ item }">
      <td>
        <FlotoTechnicianPicker
          :value="item.technicianId"
          class="cursor-default"
          disabled
          placeholder="---"
          :additional-options="cxoUsers"
        />
      </td>
    </template>
    <template v-slot:startDate="{ item }">
      <td class="text-ellipsis">{{ item.timeRange.startTime | datetime }}</td>
    </template>
    <template v-slot:endDate="{ item }">
      <td class="text-ellipsis">
        {{
          !item.timeRange.endTime ? '---' : item.timeRange.endTime | datetime
        }}
      </td>
    </template>
    <template v-slot:timeTaken="{ item, update }">
      <td class="text-ellipsis">
        <!-- <FlotoDot
          v-if="item.workLogState === 'start' || item.workLogState === 'pause'"
          class="mx-1"
          :bg="
            item.workLogState === 'start'
              ? colors.success
              : colors['neutral-light']
          "
        /> -->
        {{ calculateTimeTaken(item) }}
        <template
          v-if="
            item.timeBasedWorkLog &&
            (isUpdateable(item) || item.technicianId === user.id)
          "
        >
          <FlotoDeleteBtn
            v-if="
              item.workLogState === 'start' || item.workLogState === 'pause'
            "
            class="mx-1"
            :message="$t('confirm_stop_item', { item: $t('timer') })"
            @confirm="update({ ...item, workLogState: 'stop' })"
          >
            <template v-slot:trigger>
              <a href="javascript:;">
                <MTooltip>
                  <template v-slot:trigger>
                    <button
                      :aria-label="$t('stop')"
                      class="control-button stop"
                    >
                      <MIcon size="md" name="stop" />
                    </button>
                  </template>
                  {{ $t('stop') }}
                </MTooltip>
              </a>
            </template>
          </FlotoDeleteBtn>
          <FlotoDeleteBtn
            v-if="item.workLogState === 'pause'"
            class="mx-1"
            :message="$t('confirm_start_item', { item: $t('timer') })"
            @confirm="update({ ...item, workLogState: 'start' })"
          >
            <template v-slot:trigger>
              <a href="javascript:;">
                <MTooltip>
                  <template v-slot:trigger>
                    <button
                      :aria-label="$t('start')"
                      class="control-button play"
                    >
                      <MIcon size="md" name="play" />
                    </button>
                  </template>
                  {{ $t('start') }}
                </MTooltip>
              </a>
            </template>
          </FlotoDeleteBtn>
          <FlotoDeleteBtn
            v-if="item.workLogState === 'start'"
            class="mx-1"
            :message="$t('confirm_pause_item', { item: $t('timer') })"
            @confirm="update({ ...item, workLogState: 'pause' })"
          >
            <template v-slot:trigger>
              <a href="javascript:;">
                <MTooltip>
                  <template v-slot:trigger>
                    <button
                      :aria-label="$t('pause')"
                      class="control-button pause"
                    >
                      <MIcon size="md" name="pause" />
                    </button>
                  </template>
                  {{ $t('pause') }}
                </MTooltip>
              </a>
            </template>
          </FlotoDeleteBtn>
        </template>
      </td>
    </template>
    <template v-slot:description="{ item }">
      <MTooltip :disabled="item.description.length <= 20">
        <template v-slot:trigger>
          <td class="text-ellipsis">{{ item.description }}</td>
        </template>
        {{ item.description }}
      </MTooltip>
    </template>
    <template v-slot:actions="{ item, edit, remove, update }">
      <td>
        <div
          v-if="isUpdateable(item) || item.technicianId === user.id"
          class="flex items-center"
        >
          <MTooltip>
            <template v-slot:trigger>
              <MButton
                variant="transparent"
                class="small-icon-button cursor-pointer"
                :aria-label="$t('edit')"
                @click="edit"
              >
                <MIcon name="pencil" size="lg" />
              </MButton>
            </template>
            {{ $t('edit') }}
          </MTooltip>
          <FlotoDeleteBtn
            class="mx-1"
            size="lg"
            only-icon
            :message="$t('confirm_remove_item', { item: $t('work_log') })"
            @confirm="remove"
          >
          </FlotoDeleteBtn>
        </div>
        <div v-else>---</div>
      </td>
    </template>
  </FlotoCrudContainer>
</template>

<script>
import Moment from 'moment'
import Bus from '@utils/emitter'
import { colors } from '@utils/color'
import { authComputed } from '@state/modules/auth'
import { TechnicianComputed } from '@state/modules/technician'
import WorkLogForm from './worklog-form'
import {
  getWorklogApi,
  getProjectWorklogApi,
  createWorklogApi,
  updateWorklogApi,
  deleteWorklogApi,
} from './api'
import ActionMenu from './action-menu.vue'
import { taskPermissionsMixin } from '@src/mixins/task-permissions'
import { LicenseComputed } from '@state/modules/license'
import { getUsersApi } from '@modules/users/users-api'
import { transformTechnicianPickerOptions } from '@data/user'

export default {
  name: 'TimeLogContainer',
  components: { WorkLogForm, ActionMenu },
  mixins: [taskPermissionsMixin],
  props: {
    disabled: { type: Boolean, default: false },
    resourceId: { type: Number, required: true },
    groupId: { type: Number, default: 0 },
    moduleName: { type: String, required: true },
    stickySearch: { type: Boolean, default: false },
    offsetTop: { type: Number, default: 0 },
    customRules: {
      type: Object,
      default() {
        return {}
      },
    },
    resource: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    this.colors = colors
    return {
      searchAffixed: false,
      cxoUsers: [],
    }
  },
  computed: {
    ...authComputed,
    ...LicenseComputed,
    ...TechnicianComputed,
    hasRequesterAccessToProject() {
      return (
        [this.$constants.PROJECT, this.$constants.TASK].indexOf(
          this.moduleName
        ) >= 0 && this.hasProjectRequesterLicense
      )
    },
    tableColumns() {
      return [
        { name: `${this.$tc('technician')}`, key: 'technicianId' },
        { name: `${this.$t('start')} ${this.$t('date')}`, key: 'startDate' },
        { name: `${this.$t('end')} ${this.$t('date')}`, key: 'endDate' },
        { name: `${this.$t('time_taken')}`, key: 'timeTaken' },
        { name: `${this.$t('description')}`, key: 'description' },
        ...(this.disabled
          ? []
          : [{ name: this.$tc('action', 2), key: 'actions', width: '10rem' }]),
      ]
    },
    defaultWorklog() {
      return {
        technicianId:
          this.customRules.allowedWorklogAccess === 'technicianId'
            ? this.resource.technicianId
            : this.user.id,
        timeRange: {
          startTime: Moment().subtract(3, 'hours').unix() * 1000,
          endTime: Moment().unix() * 1000,
        },
        timeBasedWorkLog: false,
      }
    },
    isOnlyAssigneeWorklogAccess() {
      if (
        (this.customRules || {}).allowedWorklogAccess === 'technicianId' &&
        this.resource.technicianId > 0 &&
        this.resource.technicianId === this.user.id
      ) {
        return true
      }
      return false
    },

    isOnlyTechnicianGroupWorklogAccess() {
      if (
        (this.customRules || {}).allowedWorklogAccess === 'groupId' &&
        this.resource.groupId > 0 &&
        [
          ...(this.user.groups || []),
          ...(this.user.requesterGroupId || []),
        ].indexOf(this.resource.groupId) >= 0
      ) {
        return true
      }
      return false
    },
    isAllTechnicianWorklogAccess() {
      if ((this.customRules || {}).allowedWorklogAccess === 'all_technician') {
        return this.user.userType === 'technician'
      }
      return false
    },
    isAllRequestersWorklogAccess() {
      if ((this.customRules || {}).allowedWorklogAccess === 'all_requesters') {
        return ((this.user || {}).requesterRoles || []).length > 0
      }
      return false
    },
    isAllUsersWorklogAccess() {
      if ((this.customRules || {}).allowedWorklogAccess === 'all_users') {
        if (this.user.userType === 'technician') {
          return true
        }
        return ((this.user || {}).requesterRoles || []).length > 0
      }
      return false
    },
    isAssigneeOrTechnicianGroupWorklogAccess() {
      if (
        (this.customRules || {}).allowedWorklogAccess ===
        'technician_or_technician_group'
      ) {
        return true
      }
      return false
    },
    isAllowedWorklogAddAccess() {
      if (this.customRules.isAllowedWorklogAccessForTask) {
        return true
      } else if ((this.customRules || {}).allowedWorklogAccess !== '') {
        if (
          this.isOnlyAssigneeWorklogAccess ||
          this.isOnlyTechnicianGroupWorklogAccess ||
          this.isAllTechnicianWorklogAccess ||
          this.isAllRequestersWorklogAccess ||
          this.isAllUsersWorklogAccess
        ) {
          return true
        }
        if (
          this.isAssigneeOrTechnicianGroupWorklogAccess &&
          ((this.resource.technicianId > 0 &&
            this.resource.technicianId === this.user.id) ||
            (this.resource.groupId > 0 &&
              [
                ...(this.user.groups || []),
                ...(this.user.requesterGroupId || []),
              ].indexOf(this.resource.groupId) >= 0))
        ) {
          return true
        }
        return false
      }
      return false
    },
  },
  watch: {
    isRequesterPicker: {
      immediate: true,
      handler() {
        if (this.hasRequesterAccessToProject) {
          this.getUsers()
        }
      },
    },
  },
  created() {
    const createWorklogHandler = () => {
      this.$refs.crudContainerRef.showCreateForm()
    }
    Bus.$on('create-worklog', createWorklogHandler)
    this.$once('hook:beforeDestroy', () => {
      Bus.$off('create-worklog', createWorklogHandler)
    })
  },
  methods: {
    refresh() {
      this.$refs.crudContainerRef.refresh()
    },
    getWorkLogs(limit, offset) {
      // @TODO Apply filters here
      const api =
        this.moduleName === this.$constants.PROJECT
          ? getProjectWorklogApi
          : getWorklogApi
      return api(this.moduleName, this.resourceId, limit, offset)
    },
    addWorkLog(data) {
      return createWorklogApi(this.moduleName, this.resourceId, {
        ...data,
      }).then((response) => {
        this.refresh()
        setTimeout(() => {
          this.$emit('refresh-worklog')
        })
      })
    },
    updateWorkLog(data) {
      return updateWorklogApi(this.moduleName, this.resourceId, data).then(
        (response) => {
          this.refresh()
          setTimeout(() => {
            this.$emit('refresh-worklog')
          })
        }
      )
    },
    getUsers() {
      const filter = {
        onlyCxoUser: true,
      }
      return getUsersApi('technician', filter, {}, undefined, undefined).then(
        (data) => {
          const cxoUsers = (data.items || []).map(
            transformTechnicianPickerOptions
          )
          this.cxoUsers = cxoUsers
        }
      )
    },
    removeWorkLog(data) {
      return deleteWorklogApi(this.moduleName, this.resourceId, data).then(
        (response) => {
          this.refresh()
          setTimeout(() => {
            this.$emit('refresh-worklog')
          })
        }
      )
    },
    calculateTimeTaken(item) {
      if (item.timeTaken) {
        if (item.workLogState === 'start' || item.workLogState === 'pause') {
          return Moment.utc(item.timeTaken).format('HH:mm:ss')
        }
        const timeTaken = this.$options.filters.duration(
          item.timeTaken,
          undefined,
          false
        )
        return `${timeTaken}`
      }
      return 0
    },
    isUpdateable(item) {
      if (this.customRules.isAllowedWorklogAccessForTask) {
        return true
      } else {
        const technician = this.technicianOptions.find(
          (f) => f.id === item.technicianId
        )
        if ((this.customRules || {}).allowedWorklogAccess !== '') {
          if (
            (this.isOnlyAssigneeWorklogAccess &&
              item.technicianId === this.user.id) ||
            this.isAllTechnicianWorklogAccess ||
            this.isAllRequestersWorklogAccess ||
            this.isAllUsersWorklogAccess ||
            (this.isOnlyTechnicianGroupWorklogAccess &&
              [
                ...((technician || {}).groups || []),
                ...((technician || {}).requesterGroups || []),
              ].indexOf(this.resource.groupId) >= 0)
          ) {
            return true
          }
          if (
            this.isAssigneeOrTechnicianGroupWorklogAccess &&
            ((this.resource.technicianId > 0 &&
              item.technicianId === this.resource.technicianId &&
              item.technicianId === this.user.id) ||
              ([
                ...(this.user.groups || []),
                ...(this.user.requesterGroupId || []),
              ].indexOf(this.resource.groupId) >= 0 &&
                [
                  ...((technician || {}).groups || []),
                  ...((technician || {}).requesterGroups || []),
                ].indexOf(this.groupId) >= 0))
          ) {
            return true
          }
          return false
        } else {
          return false
        }
      }
    },
  },
}
</script>
